import React from 'react'
import { push } from 'connected-react-router'
import { withTranslation, WithTranslation } from 'react-i18next'

import { showNotify } from 'utils/notify'
import { formatCurrency } from 'utils/number'
import { generateContract } from 'models/Contract'
import * as actions from 'store/cart/actions'
import { updateContract } from 'store/contracts/actions'
import * as simulatorActions from 'store/simulator/actions'
import BagInfo from './BagInfo'
import BenefitInfo from './BenefitInfo'
import CartButtons from './CartButtons'
import ContractButtons from './ContractButtons'
import ContractChangeConfirm from './ContractChangeConfirm'

interface Props {
  benefit: Benefit
  cart: Contract[]
  EditComponent: any
  simulation: Simulation
  hideBagInfo?: boolean
  family: Family[]
  dispatch(action: any): any
}

interface States {
  contract: Contract
  isEdit: boolean
  isInformative: boolean
  isNotValid?: boolean
}

class BenefitDetail extends React.Component<Props & WithTranslation, States> {
  static defaultProps = {
    hideBagInfo: false,
  }

  cartItem: Contract = null
  simulatorId = null

  constructor(props) {
    super(props)

    const { benefit, simulation } = this.props

    this.cartItem = this.getCartItem()
    let contract: Contract

    if (this.cartItem && this.cartItem.year === (benefit && (benefit as any).year)) {
      contract = generateContract({ ...this.cartItem, simulation })
    } else {
      contract = generateContract({ price: 0, benefit, simulation })
    }
    // assign new id
    contract.generateId()
    this.state = {
      contract,
      isEdit: true,
      isInformative: contract.product === 'informative' || (contract.benefit && contract.benefit.contractMethod === 'informative'),
      isNotValid: true
    }
  }

  handleValidationChange = (isNotValid: boolean) => {
    this.setState({ isNotValid })
  }

  componentDidMount() {
    this.props.dispatch(simulatorActions.addItem(this.state.contract))
  }

  componentWillUnmount() {
    this.props.dispatch(simulatorActions.removeItem(this.state.contract.id))
  }

  getCartItem = () => {
    const { benefit, cart } = this.props

    const year = (benefit as any).year
    return cart.find(item => item.benefit.modalityId === benefit.modalityId && (year ? item.year === year : true))
  }

  addToCart = () => {
    const { t } = this.props
    const { contract } = this.state

    if (!this._checkCartAmounts(contract,t)) {
      return
    }

    this.props.dispatch(actions.addItem(contract.toJSON(), contract.benefit))
  }

  _checkCartAmounts = (contract, t) => {
    if (!contract.price) {
      showNotify(t('contracts.zero_message'), 'error')
      return false
    }

    if (contract.benefit.minAmount) {
      for(const monthItem of contract.items){
        if(monthItem.price < contract.benefit.minAmount){
          showNotify(t('contracts.min_unreached'), 'error')
          return false
        }
      }
    }

    if (contract.benefit.maxAmount) {
      for(const monthItem of contract.items){
        if(monthItem.price > contract.benefit.maxAmount){
          showNotify(t('contracts.max_exceeded'), 'error')
          return false
        }
      }
    }
    // Additional validation: when payPerProduct is enabled, any month with value > 0 must be >= userBagProrate
    const { simulation } = this.props
    const payPerProduct = simulation && (simulation as any).payPerProduct
    const userBagProrate = simulation && (simulation as any).userBagProrate
    if (payPerProduct && userBagProrate !== null && userBagProrate !== undefined) {
      for (const monthItem of contract.items) {
        const monthPrice = typeof monthItem.price === 'number' ? monthItem.price : 0
        if (monthPrice > 0 && monthPrice < userBagProrate) {
          showNotify(t('products.value_less_than_prorated', { prorated: formatCurrency(userBagProrate) }) as string, 'error')
          return false
        }
      }
    }
    return true
  }

  updateCart = () => {
    const { t } = this.props
    const { contract } = this.state

    if (!this._checkCartAmounts(contract,t)) {
      return
    }

    this.props.dispatch(actions.updateItem(this.cartItem.id, contract.toJSON()))
  }

  removeFromCart = () => {
    this.props.dispatch(actions.removeItem(this.cartItem.id))
  }

  handleCartActions = action => {
    const { benefit, t } = this.props
    const { contract } = this.state

    if (action === 'add') {
      this.addToCart()
    } else if (action === 'update') {
      this.updateCart()
    } else if (action === 'remove') {
      this.removeFromCart()
    } else if (action === 'reset') {
      // eslint-disable-next-line
      this.setState({
        contract: generateContract(this.cartItem || { price: 0, benefit }),
      })
      this.props.dispatch(simulatorActions.removeItem(contract.id))

      showNotify(t('contracts.reset_message'))
    }
  }

  showConfirm = () => {
    const { t, dispatch } = this.props
    const { contract } = this.state


    if (contract.benefit.minAmount && contract.price) {
      for(const monthItem of contract.items){
        if(monthItem.price < contract.benefit.minAmount){
          showNotify(t('contracts.min_unreached'), 'error')
          return
        }
      }
    }
    if (contract.benefit.maxAmount && contract.price) {
      for(const monthItem of contract.items){
        if(monthItem.price > contract.benefit.maxAmount){
          showNotify(t('contracts.max_exceeded'), 'error')
          return
        }
      }
    }

    if (!contract.price) {
      if (!window.confirm(t('contracts.cancel_confirm'))) return
      dispatch(updateContract(this.cartItem.id, contract)).then(() => {
        dispatch(push('/home'))
      })
    } else if (contract.isCancelled()) {
      dispatch(updateContract(this.cartItem.id, contract)).then(() => {
        dispatch(push('/home'))
      })
    } else {
      // Pay-per-product validation before proceeding to confirm step
      const { simulation } = this.props
      const payPerProduct = simulation && (simulation as any).payPerProduct
      const userBagProrate = simulation && (simulation as any).userBagProrate
      if (payPerProduct && userBagProrate !== null && userBagProrate !== undefined) {
        for (const monthItem of contract.items) {
          const monthPrice = typeof monthItem.price === 'number' ? monthItem.price : 0
          if (monthPrice > 0 && monthPrice < userBagProrate) {
            showNotify(t('products.value_less_than_prorated', { prorated: formatCurrency(userBagProrate) }) as string, 'error')
            return
          }
        }
      }
      this.setState({ isEdit: false })
    }
  }

  showEdit = () => {
    this.setState({ isEdit: true })
  }

  handleChange = () => {
    const { contract } = this.state
    this.props.dispatch(
      simulatorActions.updateItem(contract)
    )
  }

  handleUpdateContract = (additionalInfo: any) => {
    const { t, dispatch } = this.props
    const { contract } = this.state

    const tempContract = generateContract({ ...contract, additionalInfo })

    if (tempContract.isMissingDocument()) {
      showNotify(
        'Falta un documento. Por favor llénalo para continuar',
        'error'
      )
      return
    }

    if (!window.confirm(t('contracts.update_confirm_message'))) return

    dispatch(updateContract(this.cartItem.id, tempContract)).then(() => {
      dispatch(push('/home'))
    })
  }

  isEditable = (): boolean => {
    if (!this.cartItem || this.cartItem.isCart()) return true

    function isVariableProduct(contract) {

      return contract
          && contract.benefit
          && contract.benefit.product
          && contract.benefit.product.category
          && contract.benefit.product.category.isDefault === false
    }

    const { contract } = this.state

    return (
        ['food','kindergarten','transport','health-insurance','saving-plan'].includes(contract.productCategory)
        || isVariableProduct(contract)
    )
  }

  renderEditDetail= () => {
    const { EditComponent, hideBagInfo, t } = this.props
    const { contract, isInformative, isNotValid } = this.state

    return (
      <div className="mb-5">
        <div className="product-info">
          {!hideBagInfo && (
            <React.Fragment>
              <BenefitInfo benefit={contract.benefit} />
              {!isInformative ? (
                <BagInfo
                  benefit={contract.benefit}
                  showEmployBag={contract.benefit.isTaxAdvantage}
                  showTopBag={!contract.benefit.isTaxAdvantage}
                />
              ) : null}
            </React.Fragment>
          )}

          {!isInformative && (
            <EditComponent
              {...this.props}
              contract={contract}
              onChange={this.handleChange}
              editable={this.isEditable()}
              onValidationChange={this.handleValidationChange}
            />
          )}

          {this.cartItem && !this.cartItem.isCart() && !isInformative && (
            <React.Fragment>
              <div>{t(this.cartItem.getStatusText())}</div>
              {this.isEditable() && (
                <ContractButtons onClick={this.showConfirm} />
              )}
            </React.Fragment>
          )}

          {(!this.cartItem || this.cartItem.isCart()) && !isInformative && (
            <CartButtons
              isUpdate={!!this.cartItem}
              onClick={this.handleCartActions}
              isNotValid={isNotValid}
            />
          )}

        </div>
        <hr />
      </div>
    )
  }

  renderOtherDetail = () => {
    const {  hideBagInfo } = this.props
    const { contract } = this.state
    return (
      <div className="mb-5">
        <div className="product-info">
          {!hideBagInfo && (
            <React.Fragment>
              <BenefitInfo benefit={contract.benefit} />
              <BagInfo
                benefit={contract.benefit}
                showEmployBag={contract.benefit.isTaxAdvantage}
                showTopBag={!contract.benefit.isTaxAdvantage}
              />
            </React.Fragment>
          )}
        </div>
        <ContractChangeConfirm
          contract={contract}
          onBack={this.showEdit}
          onUpdate={this.handleUpdateContract}
        />
      </div>
    )
  }

  render() {
    const { isEdit } = this.state

    this.cartItem = this.getCartItem()

    return isEdit ? this.renderEditDetail() : this.renderOtherDetail()
  }
}

export default withTranslation()(BenefitDetail)
